import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Clock, MinusCircle, Archive, RefreshCw, Calendar, ChevronDown, Share2, AlertTriangle } from "lucide-react";
import { countryToFlag } from "../lib/countryFlag";
import { relativeDayLabel } from "../lib/dateFormat";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const OutcomeBadge = ({ outcome, outcomeState }) => {
  const common = "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.06em] border";
  // Iter 209f — `outcomeState` ("won"/"lost"/"void"/"pending") is the
  // explicit per-market label.  Fall back to the legacy boolean
  // `outcome` if older clients aren't sending it yet.
  const state = outcomeState
    || (outcome === true ? "won" : outcome === false ? "lost" : "pending");
  if (state === "won")
    return (
      <span className={`${common} bg-success/10 text-success border-success/40`} data-testid="outcome-hit">
        <CheckCircle2 className="w-3 h-3" /> HIT
      </span>
    );
  if (state === "lost")
    return (
      <span className={`${common} bg-destructive/10 text-destructive border-destructive/40`} data-testid="outcome-miss">
        <XCircle className="w-3 h-3" /> MISS
      </span>
    );
  if (state === "void")
    return (
      <span className={`${common} bg-muted/40 text-muted-foreground border-border`} data-testid="outcome-void">
        <MinusCircle className="w-3 h-3" /> VOID
      </span>
    );
  return (
    <span className={`${common} bg-muted text-muted-foreground border-border`} data-testid="outcome-pending">
      <Clock className="w-3 h-3" /> PENDING
    </span>
  );
};

const formatDay = (iso) => {
  if (!iso) return "—";
  // Iter 134h — Use the shared local-calendar helper so dates aren't
  // off-by-one for users west of UTC.
  return relativeDayLabel(iso);
};

const StatTile = ({ label, value, sub, tone = "default", testid }) => (
  <div className="nd-card p-4" data-testid={testid}>
    <p className="eyebrow">{label}</p>
    <p className={`mt-2 font-display font-bold text-3xl font-tabular ${
      tone === "success" ? "text-success" : tone === "warning" ? "text-warning" : "text-foreground"
    }`}>
      {value}
      {sub && <span className="text-base text-muted-foreground font-normal"> {sub}</span>}
    </p>
  </div>
);

const HistoryPage = () => {
  const [days, setDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Iter 145bi — Engine switcher: "classic" vs "quantum".  Each value
  // hits the matching backend path (`/history/days` vs
  // `/quantum/history/days`).  We do NOT mix sources — switching the
  // engine fully reloads the archive for that engine.
  const [engine, setEngine] = useState("classic");

  // 2026-11 (EFL Trophy fix) — League-review card data.  Pulls the
  // last 24 h of parlay / single-bet legs that failed with
  // `league_not_found` / `country_not_found` and, for each, what
  // leagues Betway currently menus for that country.
  const [leagueReview, setLeagueReview] = useState(null);

  const apiPrefix = engine === "quantum" ? "/quantum/history" : "/history";

  const loadDays = async () => {
    try {
      const r = await axios.get(`${API}${apiPrefix}/days`);
      const list = r.data?.days || [];
      setDays(list);
      if (list.length > 0) setSelectedDate(list[0].date);
      else { setDayData(null); setLoading(false); }
      return list;
    } catch (e) {
      toast.error("Failed to load history");
      setLoading(false);
      return [];
    }
  };

  const loadDay = async (date) => {
    if (!date) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}${apiPrefix}/day?date=${date}`);
      setDayData(r.data);
    } catch (e) {
      toast.error("Failed to load day archive");
    } finally { setLoading(false); }
  };

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      if (engine === "quantum") {
        await axios.post(`${API}/quantum/accuracy/settle`);
      } else {
        await Promise.allSettled([
          axios.post(`${API}/history/backfill`),
          axios.post(`${API}/accuracy/settle`),
        ]);
      }
      toast.success("Refreshing archive — results update within a minute");
      setTimeout(async () => {
        await loadDays();
        if (selectedDate) await loadDay(selectedDate);
      }, 3000);
    } catch (e) {
      toast.error("Refresh failed");
    } finally { setRefreshing(false); }
  };

  useEffect(() => { loadDays(); }, [engine]);
  useEffect(() => { if (selectedDate) loadDay(selectedDate); }, [selectedDate, engine]);

  // 2026-11 — load the League-review card (fire-and-forget; empty on
  // non-admin accounts since the endpoint is auth-gated and may 403).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/autopilot/league-review?hours=48`);
        if (!cancelled) setLeagueReview(r.data || null);
      } catch {
        if (!cancelled) setLeagueReview(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hasLiveFixture = useMemo(() => {
    if (!dayData?.predictions) return false;
    const now = Date.now();
    return (dayData.predictions || []).some((p) => {
      if (p.actual_score || !p.kickoff) return false;
      const ko = new Date(p.kickoff).getTime();
      if (isNaN(ko)) return false;
      const mins = (now - ko) / 60000;
      return mins >= 0 && mins <= 150;
    });
  }, [dayData]);

  // Auto-settle on load: if ANY fixture is "pending" but kicked off ≥90min
  // ago we trigger a one-shot settle pass.  This fixes the original bug
  // where finished games sat at pending for up to 23 hours.
  useEffect(() => {
    if (!dayData?.predictions) return;
    const now = Date.now();
    const staleUngraded = (dayData.predictions || []).some((p) => {
      if (p.actual_score || !p.kickoff) return false;
      const ko = new Date(p.kickoff).getTime();
      if (isNaN(ko)) return false;
      const mins = (now - ko) / 60000;
      return mins >= 90;  // should already have an FT result
    });
    if (!staleUngraded) return;
    let cancelled = false;
    (async () => {
      const settleUrl = engine === "quantum" ? "/quantum/accuracy/settle" : "/accuracy/settle";
      try { await axios.post(`${API}${settleUrl}`); } catch {}
      await new Promise((r) => setTimeout(r, 3000));
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}${apiPrefix}/day?date=${selectedDate}`);
        if (!cancelled) setDayData(r.data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [selectedDate, engine, apiPrefix, dayData]);

  useEffect(() => {
    if (!hasLiveFixture) return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") {
        timer = setTimeout(tick, 120000); return;
      }
      const settleUrl = engine === "quantum" ? "/quantum/accuracy/settle" : "/accuracy/settle";
      try { await axios.post(`${API}${settleUrl}`); } catch {}
      await new Promise((r) => setTimeout(r, 4000));
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}${apiPrefix}/day?date=${selectedDate}`);
        if (!cancelled) setDayData(r.data);
      } catch {}
      timer = setTimeout(tick, 120000);
    };
    timer = setTimeout(tick, 120000);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [hasLiveFixture, selectedDate, engine, apiPrefix]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />

      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 nd-fade-up">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="eyebrow flex items-center gap-2">
              <Archive className="w-3.5 h-3.5" /> Full archive
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.03em] mt-2">
              Prediction Archive
            </h1>
            {/* Iter 145bi — Engine switcher: side-by-side archive for Quantum.  */}
            <div
              className="mt-4 inline-flex items-center gap-0 rounded-full border border-border bg-card p-0.5"
              data-testid="history-engine-switch"
            >
              {[
                { key: "classic", label: "Classic" },
                { key: "quantum", label: "Quantum" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setEngine(opt.key)}
                  data-testid={`history-engine-${opt.key}`}
                  className={`px-4 py-1.5 text-xs font-bold uppercase tracking-[0.1em] rounded-full transition ${
                    engine === opt.key
                      ? (opt.key === "quantum"
                          ? "bg-cyan-500 text-cyan-950"
                          : "bg-foreground text-background")
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {hasLiveFixture && (
              <span
                className="mt-3 inline-flex items-center gap-2 text-xs text-destructive bg-destructive/10 border border-destructive/40 rounded-full px-3 py-1"
                data-testid="history-live-chip"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />
                Live · auto-updating every 2 min
              </span>
            )}
          </div>
          <Button
            variant="outline"
            onClick={refreshNow}
            disabled={refreshing}
            data-testid="history-refresh-btn"
            className="h-9 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing" : "Refresh"}
          </Button>
          {selectedDate && (
            <Button
              variant="outline"
              onClick={async () => {
                const url = `${window.location.origin}/share/${selectedDate}`;
                const title = `NDAKUBET Track Record · ${selectedDate}`;
                const text = dayData?.summary?.hit_rate != null
                  ? `${dayData.summary.hit_rate}% hit rate on ${selectedDate}`
                  : `Predictions track record · ${selectedDate}`;
                try {
                  if (navigator.share) {
                    await navigator.share({ url, title, text });
                  } else {
                    await navigator.clipboard.writeText(url);
                    toast.success("Share link copied");
                  }
                } catch {/* user cancelled */}
              }}
              data-testid="history-share-btn"
              className="h-9 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
            >
              <Share2 className="w-4 h-4 mr-1.5" />
              Share this day
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {leagueReview && Array.isArray(leagueReview.items) && leagueReview.items.length > 0 && (
          <section
            className="nd-card p-5 mb-6 border-warning/40 bg-warning/[0.03]"
            data-testid="league-review-card"
          >
            <header className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 shrink-0 text-warning mt-0.5" />
              <div className="flex-1">
                <h3 className="font-display text-lg font-bold tracking-[-0.01em]">
                  League review · {leagueReview.items.length} league{leagueReview.items.length === 1 ? "" : "s"} not on Betway
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Legs in the last {leagueReview.hours || 48} h that failed with
                  <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-foreground text-[11px]">league_not_found</code>
                  or
                  <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-foreground text-[11px]">country_not_found</code>.
                  Map the league to its real Betway name, or switch it off in the whitelist.
                </p>
              </div>
            </header>
            <div className="space-y-3" data-testid="league-review-items">
              {leagueReview.items.map((row, idx) => {
                const alt = (leagueReview.betway_leagues_by_country || {})[row.country] || [];
                return (
                  <div
                    key={`${row.league}|${row.country}|${idx}`}
                    className="rounded-xl border border-border bg-card p-4"
                    data-testid={`league-review-row-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-base flex items-center gap-2 flex-wrap">
                          <span className="mr-0.5" aria-hidden>{countryToFlag(row.country)}</span>
                          <span className="truncate">{row.league || "Unknown league"}</span>
                          <Badge variant="outline" className="border-destructive/40 text-destructive bg-destructive/5 text-[10px]">
                            {row.reason}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {row.country || "No country"} · {row.count} failed leg{row.count === 1 ? "" : "s"}
                          {row.last_at ? ` · last ${new Date(row.last_at).toLocaleString()}` : ""}
                        </div>
                      </div>
                    </div>
                    {row.fixtures && row.fixtures.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <span className="eyebrow mr-1">Fixtures:</span>
                        {row.fixtures.slice(0, 3).join(" · ")}
                        {row.fixtures.length > 3 ? ` · +${row.fixtures.length - 3}` : ""}
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="eyebrow">Betway currently menus for {row.country || "this country"}:</p>
                      {alt.length === 0 ? (
                        <p className="text-xs text-muted-foreground mt-1" data-testid={`league-review-no-alts-${idx}`}>
                          No leagues seen in the last 48 h — Betway may not list this country right now.
                        </p>
                      ) : (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`league-review-alts-${idx}`}>
                          {alt.slice(0, 20).map((a) => (
                            <Badge
                              key={a.normalized || a.name}
                              variant="outline"
                              className="border-border bg-subtle text-foreground text-[11px]"
                            >
                              {a.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {days.length > 0 && (() => {
          // Iter 134 — Dedupe by date (some days have multiple snapshots
          // saved under different day_keys).  Sum the pick counts.
          const grouped = days.reduce((acc, d) => {
            acc[d.date] = (acc[d.date] || 0) + (d.count || 0);
            return acc;
          }, {});
          const orderedDates = Object.keys(grouped).sort().reverse();
          const VISIBLE = 4;        // 4 named pills, rest under "More ▾"
          const visible = orderedDates.slice(0, VISIBLE);
          const more = orderedDates.slice(VISIBLE);
          return (
            <div
              className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-card mb-6 flex-wrap"
              data-testid="history-day-tabs"
            >
              {visible.map((date) => {
                const active = selectedDate === date;
                return (
                  <button
                    key={date}
                    onClick={() => setSelectedDate(date)}
                    className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors ${
                      active
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`history-day-${date}`}
                  >
                    {formatDay(date)}
                    <span className={`font-tabular ${active ? "opacity-80" : "opacity-60"}`}>· {grouped[date]}</span>
                  </button>
                );
              })}
              {more.length > 0 && (() => {
                const moreActive = more.includes(selectedDate);
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      data-testid="history-more-dates-trigger"
                      className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors ${
                        moreActive
                          ? "bg-foreground text-background"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      {moreActive ? formatDay(selectedDate) : "More dates"}
                      <ChevronDown className="w-3 h-3 -mr-0.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className="min-w-[180px]"
                      data-testid="history-more-dates-menu"
                    >
                      {more.map((date) => (
                        <DropdownMenuItem
                          key={date}
                          onSelect={() => setSelectedDate(date)}
                          data-testid={`history-day-${date}`}
                          className={`cursor-pointer flex items-center justify-between gap-3 py-2 ${
                            selectedDate === date ? "bg-foreground/[0.06] font-semibold" : ""
                          }`}
                        >
                          <span>{formatDay(date)}</span>
                          <span className="text-[11px] text-muted-foreground font-tabular">{grouped[date]} picks</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}
            </div>
          );
        })()}

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground" />
          </div>
        )}

        {!loading && days.length === 0 && (
          <div className="nd-card p-10 text-center" data-testid="history-empty">
            <Archive className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-display text-xl font-semibold">No archive yet</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              The archive keeps the entire history of predictions with their actual results — no date limit.
              Click <span className="text-foreground font-semibold">Refresh</span> to pull any
              matches that have just finished.
            </p>
          </div>
        )}

        {!loading && dayData && (
          <>
            {dayData.summary &&
              dayData.summary.fixtures_total > 0 &&
              (dayData.summary.fixtures_settled || 0) === 0 && (
                <div
                  className="rounded-xl border border-border bg-subtle p-4 mb-6 text-sm flex items-start gap-3"
                  data-testid="history-pending-banner"
                >
                  <Clock className="w-5 h-5 shrink-0 mt-0.5 text-muted-foreground" />
                  <div>
                    <div className="font-semibold">All matches are still pending</div>
                    <div className="text-muted-foreground text-xs mt-1">
                      None of the {dayData.summary.fixtures_total} matches on this day have finished yet.
                      HIT / MISS badges appear automatically after each game ends — click
                      Refresh, or come back after kickoff.
                    </div>
                  </div>
                </div>
              )}

            {dayData.summary && (
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
                data-testid="history-summary"
              >
                <StatTile label="Fixtures" value={dayData.summary.fixtures_total} testid="stat-fixtures" />
                <StatTile label="Settled" value={dayData.summary.fixtures_settled} testid="stat-settled" />
                <StatTile
                  label="Hits"
                  value={dayData.summary.markets_correct}
                  sub={`/ ${dayData.summary.markets_graded}`}
                  tone="success"
                  testid="stat-hits"
                />
                <StatTile
                  label="Hit rate"
                  value={dayData.summary.hit_rate != null ? `${dayData.summary.hit_rate}%` : "—"}
                  tone="warning"
                  testid="stat-hit-rate"
                />
              </div>
            )}

            <div className="space-y-4" data-testid="history-match-list">
              {(dayData.predictions || []).map((p) => (
                <article
                  key={p.fixture_id}
                  className="nd-card nd-card-hover p-5"
                  data-testid={`history-match-${p.fixture_id}`}
                >
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge variant="outline" className="border-border text-muted-foreground bg-transparent text-[11px]">
                        <span className="mr-1" aria-hidden>{countryToFlag(p.league_country)}</span>
                        {p.league_name}
                      </Badge>
                      {p.actual_score ? (
                        <span className="font-display font-bold text-foreground text-lg font-tabular" data-testid={`final-score-${p.fixture_id}`}>
                          {p.actual_score.home}–{p.actual_score.away}
                        </span>
                      ) : (
                        <span className="eyebrow">Not finished</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground font-tabular">
                      {new Date(p.kickoff).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      {p.home_logo && <img src={p.home_logo} alt="" className="w-8 h-8 object-contain" />}
                      <span className="text-sm font-display font-semibold">{p.home_team}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {p.away_logo && <img src={p.away_logo} alt="" className="w-8 h-8 object-contain" />}
                      <span className="text-sm font-display font-semibold">{p.away_team}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(p.markets || []).slice(0, 6).map((mk, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border bg-subtle text-xs"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-muted-foreground truncate">{mk.market_type}</div>
                          <div className="text-foreground font-semibold truncate">
                            {mk.prediction}{" "}
                            <span className="text-muted-foreground font-tabular">({mk.confidence}%)</span>
                          </div>
                        </div>
                        <OutcomeBadge outcome={mk.outcome} outcomeState={mk.outcome_state} />
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>

            {dayData.predictions?.length === 0 && (
              <div className="nd-card p-10 text-center">
                <p className="text-muted-foreground">No predictions archived for this date.</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default HistoryPage;
